import React, { useContext } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';

import { Link } from 'react-router-dom';
import MasterLayout from '../components/Layout';
import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { sort_by } from '../utility/index';
import { useAppState } from '../../index.js';
import strings from '../utility/strings.js';

const GET_LOCATIONS = gql`
  query GetHoursLocations {
    diningCourtCategories {
      name,
      diningCourts {
        id,
        category,
        name,
        formalName,
				normalHours {
					id
					name
					effectiveDate
					days {
						dayOfWeek
						meals {
							endTime
							name
							startTime
						}
					}
				}
      }
    }
  }
`;

function createUID() {
  return Math.floor(Math.random() * 100000000)
}

const Loading = (props) => {
  const { styleprops } = props;
  let array = [1, 2, 3]
  const loadingContent = array.map((x) => {
    return <div className="location-hours" key={x}>
      <div className="location-hours-header">
        <div className="hours-header-title"><Skeleton width="20%" height={75} style={{ margin: '0 auto' }} /></div>
        <div className="hours-header-term">

        </div>
      </div>
      <div className="location-hours-section">
        <div className="hours-section-title"><div style={{ padding: '.5rem 2rem', width: '50%', margin: '0 auto', border: '1px solid #daaa00' }}><Skeleton width="50%" style={{ margin: '0 auto' }} height={40} component="p" /></div></div>
        <div className="location-hours-section-meals">
          <div className="hours-section-row">
            <Skeleton width="100%" height={25} />
          </div>
          <div className="hours-section-row">
            <Skeleton width="100%" height={25} />
          </div>
          <div className="hours-section-row">
            <Skeleton width="100%" height={25} />
          </div>
        </div>
      </div>
    </div>
  })
  return <main className="mdl-layout__content" style={{ ...styleprops, paddingTop: 68 }} id="main">
    <div className="mdl-layout__content-page">
      <section aria-label="Dining locations operating hours">
        <div className="locations-hours">
        {loadingContent}
        </div>
      </section>
    </div>
  </main>
}

const Hours = (props) => {
  const { locations, currentUser, GET_USER } = useAppState();
  const { AppContext } = props;
  const { loading, data } = useQuery(GET_LOCATIONS); // Removed unused 'error' variable
  return (
    <>
      <Helmet>
        <title>Hours of Operation - Purdue Dining Menus</title>
        <meta name="description" content="View operating hours for all Purdue University dining courts, quick bites, and on-the-go locations across campus." />
        <meta name="keywords" content="dining hours, restaurant hours, Purdue dining schedule, campus dining times" />
        <meta property="og:title" content="Hours of Operation - Purdue Dining" />
        <meta property="og:description" content="View operating hours for all Purdue University dining locations." />
      </Helmet>
      <MasterLayout AppContext={AppContext} locations={locations} currentUser={currentUser} GET_USER={GET_USER} pageName="Hours of Operation">
        {!loading ? <HoursContent AppContext={AppContext} loading={loading} data={data} /> : <Loading /> }
      </MasterLayout>
    </>
  )
}

// Props received from Layout will be accessible through here
const HoursContent = (props) => {
  const { AppContext, data } = props;
  const { styleprops } = useContext(AppContext);

  function renderBlank() {
    return (
      <div className="location-hours-section" >
        <div className="hours-section-title">{strings.hoursunavailable}</div>
        <div className="location-hours-section-meals"></div>
      </div>
    )
  }

  function renderDays(term) {
    let week = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    let days = sort_by(term.days, week, 'dayOfWeek');
    let periods = [];


    if (term.days.length === 0 && term.name) {
      return (
        <div className="location-hours">
          <div key={"locationHours_" + Math.floor(Math.random() * 999999)} className="location-hours-section">
            <div className="hours-section-title"><span>Monday-Sunday</span></div>
            <div className="location-hours-section-meals">
              <div key={"hoursSection_" + Math.floor(Math.random() * 999999)} className="hours-section-row">
                <div className="hours-section-row-label">All Meals</div>
                <div className="hours-section-row-text">Closed</div>
              </div>
            </div>
          </div>
          <div className="location-hours-section">
            <div className="hours-section-disclaimer"><span>{strings.hoursdisclaimer}<br />{strings.hoursdislaimertimezone}</span></div>
          </div>
        </div>
      );
    }

    days.forEach(function (day, index) {

      let lastPeriod = periods.length > 0 ? periods[periods.length - 1] : null;

      // We have to sort the meals otherwise the strings will not match on compare
      let lastMeals = lastPeriod ? lastPeriod.meals : null;
      let dayMeal = day.meals;

      if (index === 0 || (lastMeals !== null && JSON.stringify(lastMeals) !== JSON.stringify(dayMeal))) {
        periods.push({
          startDay: day.dayOfWeek,
          endDay: day.dayOfWeek,
          meals: day.meals
        });
      } else {
        lastPeriod.endDay = day.dayOfWeek
      }
    });

    let sections;
    if (periods.length > 0) {
      sections = periods.map(function (period) {
        let timeParseFormat = "HH:mm:ss";
        let timeFormat = "h:mma";
        let periodName = period.startDay !== period.endDay ? period.startDay + " - " + period.endDay : period.startDay;
        let meals = period.meals.map(function (meal) {

          let mealName = meal.name;

          let startTime = meal.startTime;
          let startTimeText = startTime ? (new moment.parseZone(startTime, timeParseFormat).format(timeFormat)).slice(0, -1) : null;

          let endTime = meal.endTime;
          let endTimeText = startTime ? (new moment.parseZone(endTime, timeParseFormat).format(timeFormat)).slice(0, -1) : null;

          let rowText = startTime ? <span> {startTimeText} &#8211; {endTimeText} </span> : "Closed";

          return <div key={periodName + "_" + meal.name + "-" + createUID()} className="hours-section-row" role="listitem">
            <span className="hours-section-row-label">{mealName}</span>
            <span className="hours-section-row-text">{rowText}</span>
          </div>
        });

        return <section key={periodName + "-" + createUID()} className="location-hours-section" aria-labelledby={`period-${periodName.replace(/\s+/g, '-').toLowerCase()}`}>
          <h3 className="hours-section-title" id={`period-${periodName.replace(/\s+/g, '-').toLowerCase()}`}><span>{periodName}</span></h3>
          <div className="location-hours-section-meals" role="list" aria-label={`Meal times for ${periodName}`}>{meals}</div>
        </section>
      });
    } else {
      sections = (
        <section className="location-hours-section" aria-label="Hours unavailable">
          <h3 className="hours-section-title">{strings.hoursunavailable}</h3>
          <div className="location-hours-section-meals"></div>
        </section>
      )
    }
    return (
      <div>
        {sections}
        {periods.length > 0 ?
          <aside className="location-hours-section" role="note" aria-label="Hours disclaimer">
            <p className="hours-section-disclaimer"><span>{strings.hoursdisclaimer}<br />{strings.hoursdislaimertimezone}</span></p>
          </aside> : ""}
      </div>
    );
  }

  const DiningCourts = (props) => {
    const { formalName, name, normalHours, keyid } = props; // Removed unused 'id' variable

    let dateURL = moment().format("YYYY/M/D")
    return <article key={keyid} className="location-hours" aria-labelledby={`${name}-hours-title`}>

      <header className="location-hours-header">
        <h2 className="hours-header-title" id={`${name}-hours-title`}><Link style={{ textDecoration: "none" }} to={"/" + name + "/" + dateURL + "/"}>{formalName}</Link></h2>
        <div className="hours-header-term">{normalHours.length !== 0 && normalHours[0].length !== 0 ? normalHours[0].name : null}</div>
      </header>
      {normalHours.length !== 0 ? renderDays(normalHours[0], name) : renderBlank()}
    </article>
  }

  const listOrder = ["Dining Courts", "Quick Bites", "On-the-GO!"];
  const locationBlocks = [];
  listOrder.forEach((listOrderName, i) => {
    const category = data.diningCourtCategories.filter(y => {
      return y.name === listOrderName
    });

    category[0].diningCourts.forEach(x => {
      locationBlocks.push(<DiningCourts {...x} key={x.formalName + '-' + createUID()} />)
    })
  });

  return <main className="mdl-layout__content-page" style={{ ...styleprops, paddingTop: 68, backgroundColor: '#222' }} id="main">
    <section aria-label="Dining locations operating hours" role="region">
      <div className="locations-hours">
        {locationBlocks}
      </div>
    </section>
  </main>
}

export default Hours;